import { useEffect, useRef, useState } from "react";
import { Button, Input } from "@oc-labs/wt-storybook";
import ModalV2 from "../ModalV2";
import TagInput from "../TagInput";
import Toggle from "../Toggle";
import useModal from "../../hooks/useModal";
import { Upload } from "lucide-react";

export default function ModalEditToken({ networks = [], abis = {}, onEdit }) {
    const modal = useModal("editToken");
    const token = modal.data?.token;
    const fileInputRef = useRef(null);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [symbol, setSymbol] = useState("");
    const [decimals, setDecimals] = useState("18");
    const [networkId, setNetworkId] = useState("");
    const [logo, setLogo] = useState("");
    const [abiSource, setAbiSource] = useState("");
    const [customAbi, setCustomAbi] = useState(null);
    const [customAbiName, setCustomAbiName] = useState("");
    const [abiError, setAbiError] = useState("");
    const [tags, setTags] = useState([]);
    const [fixedPrice, setFixedPrice] = useState("");
    const [fixedPriceTouched, setFixedPriceTouched] = useState(false);
    const [buyable, setBuyable] = useState(false);
    const [buyableTouched, setBuyableTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fixedPriceValid = fixedPrice === "" || /^\d+(\.\d{1,18})?$/.test(fixedPrice);

    const abiKeys = Object.keys(abis);

    useEffect(() => {
        if (token) {
            setName(token.name || "");
            setDescription(token.description || "");
            setSymbol(token.symbol || "");
            setDecimals(String(token.decimals ?? 18));
            setNetworkId(token.networkId || "");
            setLogo(token.logo || "");
            const initialAbi = token.abiURL || "";
            setAbiSource(abis[initialAbi] || initialAbi);
            setCustomAbi(null);
            setCustomAbiName("");
            setAbiError("");
            setTags(Array.isArray(token.tags) ? token.tags : []);
            setFixedPrice(
                token.fixedPrice === null || token.fixedPrice === undefined
                    ? ""
                    : String(token.fixedPrice)
            );
            setFixedPriceTouched(false);
            setBuyable(Boolean(token.buyable));
            setBuyableTouched(false);
        }
    }, [token, abis]);

    const handleClose = () => {
        setName("");
        setDescription("");
        setSymbol("");
        setDecimals("18");
        setNetworkId("");
        setLogo("");
        setAbiSource("");
        setCustomAbi(null);
        setCustomAbiName("");
        setAbiError("");
        setTags([]);
        setFixedPrice("");
        setFixedPriceTouched(false);
        setBuyable(false);
        setBuyableTouched(false);
        setSubmitting(false);
        modal.close();
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const parsed = JSON.parse(evt.target.result);
                setCustomAbi(parsed);
                setCustomAbiName(file.name);
                setAbiSource("custom");
                setAbiError("");
            } catch {
                setAbiError("Invalid JSON file");
                setCustomAbi(null);
                setCustomAbiName("");
            }
        };
        reader.readAsText(file);
    };

    const getAbiURL = () => {
        if (abiSource === "custom") return customAbi;
        return abiSource || null;
    };

    const savedAbiDisplay = (() => {
        const saved = token?.abiURL;
        if (!saved || typeof saved !== "string") return null;
        if (Object.values(abis).includes(saved)) return null;
        return saved;
    })();

    const handleSubmit = async () => {
        if (!name.trim() || !symbol.trim() || !networkId || !getAbiURL()) return;
        if (!fixedPriceValid) return;
        setSubmitting(true);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                symbol: symbol.trim().toUpperCase(),
                decimals: parseInt(decimals) || 18,
                networkId,
                logo: logo.trim() || undefined,
                abiURL: getAbiURL(),
                tags,
            };
            if (fixedPriceTouched) {
                payload.fixedPrice = fixedPrice === "" ? null : Number(fixedPrice);
            }
            if (buyableTouched) {
                payload.buyable = buyable;
            }
            await onEdit(token.id, payload);
            handleClose();
        } catch (err) {
            console.error("Error updating token:", err);
            setSubmitting(false);
        }
    };

    const isValid = name.trim() && symbol.trim() && networkId && getAbiURL() && fixedPriceValid;

    return (
        <ModalV2 name="editToken" title="Edit Token" className="w-[600px] max-h-[90vh] overflow-y-auto">
            <div className="mt-4 space-y-4">
                <Input
                    label="Token Name"
                    placeholder="e.g. My Token"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <Input
                    label="Description"
                    placeholder="Brief description of the token"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Symbol"
                        placeholder="e.g. MTK"
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    />
                    <Input
                        label="Decimals"
                        type="number"
                        placeholder="18"
                        value={decimals}
                        onChange={(e) => setDecimals(e.target.value)}
                    />
                </div>
                {token?.address && (
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Contract Address</label>
                        <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-mono text-slate-500">
                            {token.address}
                        </div>
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Network</label>
                    <select
                        value={networkId}
                        onChange={(e) => setNetworkId(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select a network</option>
                        {networks.map((n) => (
                            <option key={n.id} value={n.id}>{n.name}</option>
                        ))}
                    </select>
                </div>
                <Input
                    label="Logo URL"
                    placeholder="https://example.com/logo.png"
                    value={logo}
                    onChange={(e) => setLogo(e.target.value)}
                />
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ABI</label>
                    <select
                        value={abiSource === "custom" ? "custom" : abiSource}
                        onChange={(e) => {
                            setAbiSource(e.target.value);
                            if (e.target.value !== "custom") {
                                setCustomAbi(null);
                                setCustomAbiName("");
                                setAbiError("");
                            }
                        }}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select an ABI</option>
                        {abiKeys.map((key) => (
                            <option key={key} value={abis[key]}>{key}</option>
                        ))}
                        <option value="custom">Upload custom ABI...</option>
                    </select>
                    {abiSource === "custom" && (
                        <div className="mt-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-2 w-full rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                            >
                                <Upload size={14} />
                                {customAbiName || "Choose a JSON file..."}
                            </button>
                            {abiError && <p className="text-xs text-red-500 mt-1">{abiError}</p>}
                        </div>
                    )}
                    {savedAbiDisplay && (
                        <div className="mt-2">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Current ABI</label>
                            <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 break-all">
                                {savedAbiDisplay}
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <Input
                        label="Fixed price (USD)"
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 0.045"
                        value={fixedPrice}
                        onChange={(e) => {
                            setFixedPrice(e.target.value);
                            setFixedPriceTouched(true);
                        }}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                        Set a manual USD price for tokens without a price oracle. Leave empty to use the default price source.
                    </p>
                    {!fixedPriceValid && (
                        <p className="text-xs text-red-500 mt-1">Must be a non-negative number with up to 18 decimal places.</p>
                    )}
                </div>
                <TagInput label="Tags" value={tags} onChange={setTags} />
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex flex-col">
                        <span className="text-slate-900 text-sm font-medium">Buyable</span>
                        <span className="text-slate-500 text-xs">Available for purchase</span>
                    </div>
                    <Toggle
                        checked={buyable}
                        onChange={(value) => {
                            setBuyable(value);
                            setBuyableTouched(true);
                        }}
                        text="Buyable"
                    />
                </div>
            </div>
            <div className="mt-6 flex gap-2">
                <Button type="tertiary" onClick={handleClose} className="w-1/2">
                    Cancel
                </Button>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    className="w-1/2"
                    disabled={submitting || !isValid}
                >
                    {submitting ? "Saving..." : "Save Changes"}
                </Button>
            </div>
        </ModalV2>
    );
}
